import { Card } from '@/components/ui/card';
import logoArgos from '@/assets/logo-argos.png';
import { DatosReporte } from '@/hooks/useReporteAmbiental';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

interface ReporteFormatoICAProps {
  datos: DatosReporte;
  periodo?: string;
}

export const ReporteFormatoICA = ({ datos, periodo = '2025' }: ReporteFormatoICAProps) => {
  const { obligacion, planManejo } = datos;

  const fechaVersion = planManejo?.created_at 
    ? format(new Date(planManejo.created_at), 'dd/MM/yyyy', { locale: es })
    : 'N/A';

  // Función para obtener impactos por IDs
  const obtenerImpactosPorIds = (ids: number[]) => {
    if (!planManejo?.impactos_atendidos || !Array.isArray(ids)) return '';
    return ids
      .map(id => planManejo.impactos_atendidos.find((imp: any) => imp.id === id)?.descripcion)
      .filter(Boolean)
      .join(', ');
  };

  // Obtener observaciones generales del nuevo campo
  const observacionesGenerales = planManejo?.observaciones_generales || '';

  // Obtener items de metas (nueva estructura)
  const itemsMetas = (planManejo?.items_metas as any[]) || [];

  // Obtener items de acciones (nueva estructura)
  const itemsAcciones = (planManejo?.items_acciones as any[]) || [];

  // Calcular porcentaje de cumplimiento del programa basado en items de acciones
  const calcularPorcentajeCumplimiento = () => {
    if (itemsAcciones.length === 0) return 0;
    
    const porcentajes = itemsAcciones
      .map((item: any) => {
        const porcentaje = item.porcentaje_cumplimiento;
        if (typeof porcentaje === 'string') {
          return parseFloat(porcentaje.replace('%', '')) || 0;
        }
        return typeof porcentaje === 'number' ? porcentaje : 0;
      })
      .filter((p: number) => p > 0);
    
    if (porcentajes.length === 0) return 0;
    
    const suma = porcentajes.reduce((acc: number, val: number) => acc + val, 0);
    return Math.round(suma / porcentajes.length);
  };

  return (
    <div id="reporte-ica" className="bg-white p-8 text-black">
      {/* Encabezado */}
      <div className="border-2 border-black mb-4">
        <div className="grid grid-cols-12 border-b-2 border-black">
          <div className="col-span-3 p-2 flex items-center justify-center border-r-2 border-black">
            <img src={logoArgos} alt="Logo Argos" className="h-20 object-contain" />
          </div>
          <div className="col-span-7 p-2 flex items-center justify-center border-r-2 border-black">
            <h1 className="text-xs font-bold text-center uppercase">
              ESTADO DE CUMPLIMIENTO DE LOS PROGRAMAS QUE CONFORMAN EL PLAN DE MANEJO AMBIENTAL 
              DE LA MINA DE RIO CLARO DE CEMENTOS ARGOS - PERIODO {periodo}
            </h1>
          </div>
          <div className="col-span-2 p-2">
            <div className="text-[10px] font-semibold text-center border-b border-black pb-1 mb-1">
              FORMATO: ICA-1a
            </div>
            <div className="text-[10px] text-center">
              Hoja 1 de 1
            </div>
          </div>
        </div>
        
        {/* Información del Programa */}
        <div className="grid grid-cols-12 text-xs">
          <div className="col-span-8 border-r-2 border-black p-2">
            <div className="flex gap-2">
              <span className="font-semibold">PROGRAMA:</span>
              <span>{obligacion.nombre_programa}</span>
            </div>
          </div>
          <div className="col-span-2 border-r-2 border-black p-2">
            <div className="flex flex-col">
              <span className="font-semibold text-[10px]">VERSIÓN/FECHA:</span>
              <span className="text-[10px]">{fechaVersion}</span>
            </div>
          </div>
          <div className="col-span-2 p-2">
            <div className="flex flex-col">
              <span className="font-semibold text-[10px]">CÓDIGO PMA:</span>
              <span className="text-[10px]">{obligacion.codigo || 'N/A'}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tabla 1: Cumplimiento de Metas (Indicadores de Éxito) */}
      <div className="mb-6">
        <h2 className="text-xs font-bold mb-2 bg-gray-200 p-2 border border-black">
          CUMPLIMIENTO DE METAS (INDICADORES DE ÉXITO)
        </h2>
        <table className="w-full border-2 border-black text-[10px]">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-black p-1 w-8">N°</th>
              <th className="border border-black p-1">1. METAS</th>
              <th className="border border-black p-1" colSpan={2}>2. PARÁMETRO DE CONTROL MEDIDO</th>
              <th className="border border-black p-1" colSpan={2}>3. VALOR DE REFERENCIA O CARACTERÍSTICA DE CALIDAD</th>
              <th className="border border-black p-1" colSpan={2}>4. CUMPLIMIENTO</th>
            </tr>
            <tr className="bg-gray-100">
              <th className="border border-black p-1"></th>
              <th className="border border-black p-1">Descripción</th>
              <th className="border border-black p-1 w-[15%]">Descripción</th>
              <th className="border border-black p-1 w-[15%]">Valor</th>
              <th className="border border-black p-1 w-[15%]">Descripción</th>
              <th className="border border-black p-1 w-[15%]">Valor</th>
              <th className="border border-black p-1 w-12">SI</th>
              <th className="border border-black p-1 w-12">NO</th>
            </tr>
          </thead>
          <tbody>
            {itemsMetas.length > 0 ? (
              itemsMetas.map((item: any, index: number) => {
                // Soportar ambas estructuras: la nueva (parametros_control array de objetos) y la antigua (arrays separados)
                const parametrosDescripciones = item.parametro_control_descripciones || [];
                const parametrosValores = item.parametro_control_valores || [];
                const valoresDescripciones = item.valor_referencia_descripciones || [];
                const valoresReferencia = item.valor_referencia_valores || [];
                
                // Determinar el número de filas necesarias basado en las descripciones (que pueden ser múltiples)
                const maxFilas = Math.max(
                  parametrosDescripciones.length || 0,
                  valoresDescripciones.length || 0,
                  1
                );

                // El valor del parámetro de control es único (solo la primera celda)
                const valorParametroUnico = Array.isArray(parametrosValores) && parametrosValores.length > 0
                  ? parametrosValores[0] 
                  : '';

                // Generar numeración secuencial basada en la cantidad de impactos
                const impactosIds = item.impactos_ids || item.impactos_atendidos_ids || [];
                const numeracionImpactos = impactosIds.length > 0 
                  ? Array.from({ length: impactosIds.length }, (_, i) => i + 1).join(', ')
                  : '';

                return Array.from({ length: maxFilas }).map((_, filaIndex) => (
                  <tr key={`${index}-${filaIndex}`}>
                    {filaIndex === 0 && (
                      <>
                        <td className="border border-black p-1 text-center" rowSpan={maxFilas}>
                          {numeracionImpactos}
                        </td>
                        <td className="border border-black p-1" rowSpan={maxFilas}>
                          {item.meta_descripcion || 'Sin descripción'}
                        </td>
                      </>
                    )}
                    <td className="border border-black p-1 text-center w-[15%]">
                      {parametrosDescripciones[filaIndex] || ''}
                    </td>
                    {filaIndex === 0 && (
                      <td className="border border-black p-1 text-center w-[15%]" rowSpan={maxFilas}>
                        {valorParametroUnico}
                      </td>
                    )}
                    <td className="border border-black p-1 text-center w-[15%]">
                      {valoresDescripciones[filaIndex] || ''}
                    </td>
                    <td className="border border-black p-1 text-center w-[15%]">
                      {valoresReferencia[filaIndex] || ''}
                    </td>
                    {filaIndex === 0 && (
                      <>
                        <td className="border border-black p-1 text-center w-12" rowSpan={maxFilas}>
                          {item.cumplimiento_si_no === 'SI' ? 'X' : ''}
                        </td>
                        <td className="border border-black p-1 text-center w-12" rowSpan={maxFilas}>
                          {item.cumplimiento_si_no === 'NO' ? 'X' : ''}
                        </td>
                      </>
                    )}
                  </tr>
                ));
              })
            ) : (
              <tr>
                <td className="border border-black p-1 text-center">1</td>
                <td className="border border-black p-1 text-gray-400 italic">Sin metas registradas</td>
                <td className="border border-black p-1 w-[15%]"></td>
                <td className="border border-black p-1 text-center w-[15%]"></td>
                <td className="border border-black p-1 w-[15%]"></td>
                <td className="border border-black p-1 text-center w-[15%]"></td>
                <td className="border border-black p-1 text-center w-12"></td>
                <td className="border border-black p-1 text-center w-12"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mb-6">
        <h2 className="text-xs font-bold mb-2 bg-gray-200 p-2 border border-black">
          CUMPLIMIENTO DE LAS ACCIONES DEL PROYECTO (INDICADORES DE CUMPLIMIENTO)
        </h2>
        <table className="w-full border-2 border-black text-[10px]">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-black p-1 w-8">N°</th>
              <th className="border border-black p-1">5. ACCIONES DE MANEJO, CORRECCIÓN O COMPENSACIÓN</th>
              <th className="border border-black p-1" colSpan={2}>6. ACCIONES DE VERIFICACIÓN PERIÓDICA</th>
              <th className="border border-black p-1" colSpan={2}>7. ACCIONES DE VERIFICACIÓN SEGÚN FICHA VIGENTE</th>
              <th className="border border-black p-1">8. OBSERVACIONES</th>
            </tr>
            <tr className="bg-gray-100">
              <th className="border border-black p-1"></th>
              <th className="border border-black p-1">Descripción</th>
              <th className="border border-black p-1 w-[15%]">Periodicidad de la verificación</th>
              <th className="border border-black p-1 w-[15%]">% de cumplimiento</th>
              <th className="border border-black p-1 w-[15%]">% de avance programado</th>
              <th className="border border-black p-1 w-[15%]">% de avance a la fecha</th>
              <th className="border border-black p-1"></th>
            </tr>
          </thead>
          <tbody>
            {itemsAcciones.length > 0 ? (
              itemsAcciones.map((item: any, index: number) => (
                <tr key={index}>
                  <td className="border border-black p-1 text-center">{index + 1}</td>
                  <td className="border border-black p-1">{item.accion_descripcion || ''}</td>
                  <td className="border border-black p-1 text-center w-[15%]">{item.periodicidad_verificacion || ''}</td>
                  <td className="border border-black p-1 text-center w-[15%]">
                    {item.porcentaje_cumplimiento || '0'}{item.porcentaje_cumplimiento && !item.porcentaje_cumplimiento.toString().includes('%') ? '%' : ''}
                  </td>
                  <td className="border border-black p-1 text-center w-[15%]">
                    {item.porcentaje_avance_programado || '0'}{item.porcentaje_avance_programado && !item.porcentaje_avance_programado.toString().includes('%') ? '%' : ''}
                  </td>
                  <td className="border border-black p-1 text-center w-[15%]">
                    {item.porcentaje_avance_fecha || '0'}{item.porcentaje_avance_fecha && !item.porcentaje_avance_fecha.toString().includes('%') ? '%' : ''}
                  </td>
                  <td className="border border-black p-1">{item.observaciones || ''}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td className="border border-black p-1 text-center">1</td>
                <td className="border border-black p-1 text-gray-400 italic">Sin acciones registradas</td>
                <td className="border border-black p-1 text-center w-[15%]"></td>
                <td className="border border-black p-1 text-center w-[15%]">0%</td>
                <td className="border border-black p-1 text-center w-[15%]">0%</td>
                <td className="border border-black p-1 text-center w-[15%]">0%</td>
                <td className="border border-black p-1"></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Sección Final: Porcentaje de Cumplimiento y Responsable */}
      <div className="mb-6">
        <table className="w-full border-2 border-black text-[10px]">
          <tbody>
            <tr>
              <td className="border border-black p-2 font-bold bg-gray-100 w-1/2">
                9. PORCENTAJE DE CUMPLIMIENTO DEL PROGRAMA (%)
              </td>
              <td className="border border-black p-2 text-center">
                {calcularPorcentajeCumplimiento()}%
              </td>
            </tr>
            <tr>
              <td className="border border-black p-3 align-top">
                <div className="font-bold mb-2">Observaciones Generales</div>
                <div className="min-h-[80px] whitespace-pre-wrap">
                  {observacionesGenerales}
                </div>
              </td>
              <td className="border border-black p-3 align-top">
                <div className="font-bold mb-3">PROFESIONAL RESPONSABLE</div>
                <div className="space-y-3">
                  <div>
                    <span className="font-semibold">Nombre:</span>
                    <div className="mt-1 border-b border-gray-400 h-6"></div>
                  </div>
                  <div>
                    <span className="font-semibold">Coordinación Grupo de Gestión Ambiental y Control Fauna</span>
                  </div>
                  <div className="mt-4">
                    <span className="font-semibold">Firma:</span>
                    <div className="mt-1 border-b border-gray-400 h-6"></div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Pie de página */}
      <div className="mt-4 text-[10px] text-gray-600">
        <p>Generado el {format(new Date(), "dd/MM/yyyy 'a las' HH:mm", { locale: es })}</p>
      </div>
    </div>
  );
};
